import { createCompatibilityMatcher } from './matchpoint-compat-bloom-v2.module.js';
import { warehouse } from './matchpoint-idb-cache-manager.js';
import { HydrationService } from './matchpoint-hydration-service.js';
import {
  CollectionIndexService,
  COLLECTION_INDEX_FACET_PARTIAL_EVENT,
  EvaluationAbortedError
} from './matchpoint-collection-index-service.js';
import {
  CollectionExactAuthority,
  CompactIndexExactAuthorityAdapter,
  FullHydrationExactAuthorityAdapter
} from './matchpoint-collection-exact-authority.js';
import { FilterEngine } from './matchpoint-collection-filter-engine.js';
import { SortEngine } from './matchpoint-collection-sort-engine.js';
import { StatsEngine } from './matchpoint-collection-stats-engine.js';
import {
  COLLECTION_INDEX_PAGE_SIZE
} from './matchpoint-collection-index-paging.js';
import {
  buildMachineMatchMakerCatalogFromIndexPages,
  normalizeMachineMatchMakerCatalogProduct
} from './matchpoint-machine-match-maker-catalog.js';
import { Store } from './matchpoint-collection-state.js';
import { bus } from './matchpoint-collection-event-bus.js';
import {
  collectionReducer,
  createInitialState
} from './matchpoint-collection-reducer.js';
import {
  filterRequestCommand,
  pageChangeRequestCommand,
  exactAuthorityReadyCommand,
  exactAuthorityQuickReadyCommand
} from './matchpoint-collection-events.js';
// Phase 2 — shared render architecture (plan §Phase 2).
// The collection page already boots PathCOrchestrator + HeadTailDispatcher
// against the singleton `compactIndexStore`; MMM reuses the exact same
// module-scope singleton (populated by `CollectionIndexService.upsertMany`
// on every index-page load) so head/tail emits from MMM walk the same
// sort order and share the same sortIndex cache.
import {
  compactIndexStore,
  COMPACT_INDEX_STORE_CHANGE_EVENT
} from './matchpoint-collection-compact-index-store.js';
import { SortIndex } from './matchpoint-collection-sort-index.js';
import { PathCOrchestrator } from './matchpoint-collection-path-c-orchestrator.js';
import { HeadTailDispatcher } from './matchpoint-head-tail-dispatcher.js';
// Phase 3 — shared grid signature gate (plan §Phase 3).
// The gate owns by-ID diff detection for MMM's grid paint: identical
// visible-slice ID sequences on the same currentPage are short-circuited
// at `shouldRender` and emit `collection:grid:render-skipped` so probes
// can attribute tail-commit no-ops correctly. Replaces MMM's bespoke
// `_lastRenderedGridKey` string-compare bookkeeping.
import { GridSignatureGate } from './matchpoint-grid-signature-gate.js';

const VEHICLE_HIERARCHY_CONFIG = {
  initialField: 'type',
  sequences: {
    'Skid Steer': ['type', 'manufacturer', 'model', 'hydraulic_package'],
    Tractor: ['type', 'manufacturer', 'model', 'three_point_hitch_category', 'loader_arms', 'attachment_interface'],
    'Compact Track Loader': ['type', 'manufacturer', 'model', 'hydraulic_package'],
    'Mini Track Loader': ['type', 'manufacturer', 'model', 'hydraulic_package', 'attachment_interface'],
    'Mini Skid Steer': ['type', 'manufacturer', 'model', 'hydraulic_package', 'attachment_interface'],
    Default: ['type', 'manufacturer', 'model']
  }
};

const FILTER_CACHE_TTL_MS = 5 * 60 * 1000;
const FALLBACK_VEHICLE_ICON = window.fallbackVehicleIcon || '';
const ITEMS_PER_PAGE = 50;
const MAX_PAGINATION_LINKS = 5;
const STATUS_TEXT = Object.freeze({
  LOADING: 'Loading all attachments...',
  MATCHING: 'Matching Compatibility...',
  FILTERING: 'Filtering...'
});
const MMM_CATALOG_SOURCE_EVENT = 'matchpoint:mmm:catalog-source';
const MMM_AUTHORITY_EVENT = 'matchpoint:mmm:authority-result';
const MMM_TYPE_OPTIONS_EVENT = 'matchpoint:mmm:type-options';
const MMM_BACKFILL_EVENT = 'matchpoint:mmm:compact-index-backfill';
const MMM_BACKFILL_RETRY_DELAYS_MS = Object.freeze([0, 1000, 3000, 5000, 10000, 15000]);
const TYPE_OPTION_SOURCE_PRECEDENCE = Object.freeze({
  none: 0,
  'type-seed': 1,
  'facet-partial': 2,
  'facet-state': 3
});

const debug = {
  log: (...args) => (window.matchpointDebug?.log || console.log).call(console, '[AttachmentWizard]', ...args),
  info: (...args) => (window.matchpointDebug?.info || console.info).call(console, '[AttachmentWizard]', ...args),
  debug: (...args) => (window.matchpointDebug?.debug || console.debug).call(console, '[AttachmentWizard]', ...args),
  warn: (...args) => (window.matchpointDebug?.warn || console.warn).call(console, '[AttachmentWizard]', ...args),
  error: (...args) => (window.matchpointDebug?.error || console.error).call(console, '[AttachmentWizard]', ...args),
  group: (...args) => (window.matchpointDebug?.group || console.group).call(console, '[AttachmentWizard]', ...args),
  groupCollapsed: (...args) => (window.matchpointDebug?.groupCollapsed || console.groupCollapsed).call(console, '[AttachmentWizard]', ...args),
  groupEnd: () => (window.matchpointDebug?.groupEnd || console.groupEnd).call(console),
};

function parseJsonScript(id) {
  const element = document.getElementById(id);
  if (!element?.textContent) {
    return null;
  }

  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    debug.warn(`Failed to parse JSON metadata from #${id}`, error);
    return null;
  }
}

function toPositiveNumber(value, fallback = 0) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
}

function buildPageNumberRange(count) {
  const pageCount = toPositiveNumber(count, 0);
  return Array.from({ length: pageCount }, (_, index) => index + 1);
}

function emitCatalogSource(source, details = {}) {
  const payload = {
    source,
    at: Date.now(),
    ...details
  };

  window.__MP_MMM_DEBUG__ = window.__MP_MMM_DEBUG__ || {};
  window.__MP_MMM_DEBUG__.catalogSources = Array.isArray(window.__MP_MMM_DEBUG__.catalogSources)
    ? window.__MP_MMM_DEBUG__.catalogSources
    : [];
  window.__MP_MMM_DEBUG__.catalogSources.push(payload);
  window.__MP_MMM_DEBUG__.lastCatalogSource = payload;

  document.dispatchEvent(new CustomEvent(MMM_CATALOG_SOURCE_EVENT, {
    detail: payload
  }));
}

function recordCatalogCacheProbe(details = {}) {
  window.__MP_MMM_DEBUG__ = window.__MP_MMM_DEBUG__ || {};
  window.__MP_MMM_DEBUG__.cacheProbes = Array.isArray(window.__MP_MMM_DEBUG__.cacheProbes)
    ? window.__MP_MMM_DEBUG__.cacheProbes
    : [];
  window.__MP_MMM_DEBUG__.cacheProbes.push({
    at: Date.now(),
    ...details
  });
  window.__MP_MMM_DEBUG__.lastCatalogCacheProbe = window.__MP_MMM_DEBUG__.cacheProbes[window.__MP_MMM_DEBUG__.cacheProbes.length - 1];
}

function recordAuthorityProbe(details = {}) {
  const payload = {
    at: Date.now(),
    ...details
  };

  window.__MP_MMM_DEBUG__ = window.__MP_MMM_DEBUG__ || {};
  window.__MP_MMM_DEBUG__.authorityEvents = Array.isArray(window.__MP_MMM_DEBUG__.authorityEvents)
    ? window.__MP_MMM_DEBUG__.authorityEvents
    : [];
  window.__MP_MMM_DEBUG__.authorityEvents.push(payload);
  window.__MP_MMM_DEBUG__.lastAuthorityEvent = payload;

  document.dispatchEvent(new CustomEvent(MMM_AUTHORITY_EVENT, {
    detail: payload
  }));
}

function recordTypeOptionsProbe(details = {}) {
  const payload = {
    at: Date.now(),
    ...details
  };

  window.__MP_MMM_DEBUG__ = window.__MP_MMM_DEBUG__ || {};
  window.__MP_MMM_DEBUG__.typeOptionEvents = Array.isArray(window.__MP_MMM_DEBUG__.typeOptionEvents)
    ? window.__MP_MMM_DEBUG__.typeOptionEvents
    : [];
  window.__MP_MMM_DEBUG__.typeOptionEvents.push(payload);
  window.__MP_MMM_DEBUG__.lastTypeOptionEvent = payload;

  document.dispatchEvent(new CustomEvent(MMM_TYPE_OPTIONS_EVENT, {
    detail: payload
  }));
}

function recordBackfillProbe(details = {}) {
  const payload = {
    at: Date.now(),
    ...details
  };

  window.__MP_MMM_DEBUG__ = window.__MP_MMM_DEBUG__ || {};
  window.__MP_MMM_DEBUG__.backfillEvents = Array.isArray(window.__MP_MMM_DEBUG__.backfillEvents)
    ? window.__MP_MMM_DEBUG__.backfillEvents
    : [];
  window.__MP_MMM_DEBUG__.backfillEvents.push(payload);
  window.__MP_MMM_DEBUG__.lastBackfillEvent = payload;

  document.dispatchEvent(new CustomEvent(MMM_BACKFILL_EVENT, {
    detail: payload
  }));
}

function normalize(value) {
  return (value || '').trim().toLowerCase();
}

function normalizeTypeOptionEntries(entries = []) {
  const seenValues = new Set();
  const normalizedEntries = [];

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const label = typeof entry === 'string'
      ? entry.trim()
      : typeof entry?.value === 'string'
        ? entry.value.trim()
        : '';
    if (!label) return;

    const value = normalize(label);
    if (!value || seenValues.has(value)) return;
    seenValues.add(value);

    normalizedEntries.push({
      value,
      label,
      count: Number.isFinite(entry?.count) ? Number(entry.count) : null
    });
  });

  return normalizedEntries;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatMoney(amount) {
  const numericAmount = Number(amount || 0);
  const currencyCode = window.Shopify?.currency?.active || 'USD';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: Number.isInteger(numericAmount) ? 0 : 2,
    maximumFractionDigits: 2
  }).format(numericAmount);
}

function getFallbackIcon() {
  return FALLBACK_VEHICLE_ICON || window.Shopify?.routes?.root || '';
}

function getVehicleApi() {
  return window.Shopify?.vehicle || null;
}

function cacheKey(pathPrefix) {
  return `machine-match-maker:filters:${pathPrefix}`;
}

async function getFiltersCached(fetchAppProxy, pathPrefix) {
  const key = cacheKey(pathPrefix);

  try {
    const cachedRaw = sessionStorage.getItem(key);
    if (cachedRaw) {
      const cached = JSON.parse(cachedRaw);
      if (Date.now() - cached.ts < FILTER_CACHE_TTL_MS) {
        return cached.data;
      }
      sessionStorage.removeItem(key);
    }
  } catch (_) {
    sessionStorage.removeItem(key);
  }

  const fresh = await fetchAppProxy('filters', { pathPrefix });
  sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data: fresh }));
  return fresh;
}

function prefetchChildren(fetchAppProxy, pathPrefix, nextKey, options) {
  if (!nextKey || !Array.isArray(options)) return;

  options.slice(0, 10).forEach((option) => {
    const childPrefix = pathPrefix === 'ROOT'
      ? `${nextKey}#${option}`
      : `${pathPrefix}#${nextKey}#${option}`;

    if (sessionStorage.getItem(cacheKey(childPrefix))) return;
    getFiltersCached(fetchAppProxy, childPrefix).catch(() => {});
  });
}

function computePathAndNext(filters) {
  if (!filters || Object.keys(filters).length === 0) {
    return {
      pathPrefix: 'ROOT',
      nextFieldKey: VEHICLE_HIERARCHY_CONFIG.initialField
    };
  }

  const type = filters.type;
  const sequence = VEHICLE_HIERARCHY_CONFIG.sequences[type] || VEHICLE_HIERARCHY_CONFIG.sequences.Default;
  const orderedPairs = sequence
    .filter((key) => filters[key] !== undefined)
    .map((key) => `${key}#${filters[key]}`);

  return {
    pathPrefix: orderedPairs.join('#'),
    nextFieldKey: sequence[orderedPairs.length] || null
  };
}

function collectProducts(scope) {
  return Array.from(scope?.querySelectorAll?.('[data-attachment-product]') || []).map((element) => ({
    id: element.dataset.productId || '',
    numericId: Number(element.dataset.productId) || -1,
    element,
    type: normalize(element.dataset.productType),
    typeLabel: (element.querySelector('.attachment-wizard-item__type')?.textContent || '').trim(),
    compatibilityText: element.querySelector('[data-attachment-product-compatibility]'),
    variants: Array.from(element.querySelectorAll('[data-attachment-product-variant]'))
  }));
}

function buildProductUrl(product) {
  return product?.onlineStoreUrl || (product?.handle ? `/products/${product.handle}` : '#');
}

function renderProductMarkup(product) {
  const imageUrl = product?.featuredImage?.url;
  const imageAlt = escapeHtml(product?.featuredImage?.altText || product?.title || '');
  const productTypeLabel = escapeHtml(product?.type || 'Attachment');
  const productTitle = escapeHtml(product?.title || '');
  const productUrl = escapeHtml(buildProductUrl(product));
  const priceText = escapeHtml(formatMoney(product?.price || 0));

  return `
    <article
      class="attachment-wizard-item"
      data-attachment-product
      data-product-id="${product.id}"
      data-product-type="${escapeHtml(normalize(product?.type))}"
    >
      <a class="attachment-wizard-item__image-link" href="${productUrl}">
        ${imageUrl
          ? `<img class="attachment-wizard-item__image" src="${escapeHtml(imageUrl)}" alt="${imageAlt}" loading="lazy">`
          : '<div class="attachment-wizard-item__image attachment-wizard-item__image--placeholder"></div>'}
      </a>

      <div class="attachment-wizard-item__content">
        <div class="attachment-wizard-item__meta">
          <p class="attachment-wizard-item__type">${productTypeLabel}</p>
          <p class="attachment-wizard-item__compatibility" data-attachment-product-compatibility>
            Select a machine to confirm compatibility.
          </p>
        </div>

        <h3 class="attachment-wizard-item__title">
          <a href="${productUrl}">${productTitle}</a>
        </h3>

        <div class="attachment-wizard-item__footer">
          <p class="attachment-wizard-item__price">${priceText}</p>
          <a class="attachment-wizard-item__link" href="${productUrl}">View attachment</a>
        </div>
      </div>
    </article>
  `;
}

function createProductElement(product) {
  const template = document.createElement('template');
  template.innerHTML = renderProductMarkup(product).trim();
  return template.content.firstElementChild;
}

class AttachmentWizard {
  constructor(root) {
    this.root = root;
    this.vehicleApi = getVehicleApi();
    this.pageMeta = root.querySelector('[data-attachment-wizard-page-meta]');
    this.metadata = parseJsonScript('machine-match-maker-metadata') || {};
    this.form = root.querySelector('[data-attachment-wizard-form]');
    this.fieldsContainer = root.querySelector('[data-attachment-wizard-fields]');
    this.submitButton = root.querySelector('[data-attachment-wizard-submit]');
    this.compatibilityToggle = root.querySelector('[data-attachment-wizard-compatible-toggle]');
    this.productTypeFilter = root.querySelector('[data-attachment-wizard-type-filter]');
    this.summary = root.querySelector('[data-attachment-wizard-summary]');
    this.productsContainer = root.querySelector('[data-attachment-wizard-products]');
    this.pagination = root.querySelector('[data-attachment-wizard-pagination]');
    this.activeImage = root.querySelector('[data-attachment-wizard-active-image]');
    this.activeLabel = root.querySelector('[data-attachment-wizard-active-label]');
    this.activeName = root.querySelector('[data-attachment-wizard-active-name]');
    this.activeCopy = root.querySelector('[data-attachment-wizard-active-copy]');
    this.openModalButton = root.querySelector('[data-attachment-wizard-open-modal]');
    const initialCurrentPage = Number(this.pageMeta?.dataset.currentPage || 1);
    this.totalPages = Number(this.pageMeta?.dataset.totalPages || 1);
    this.collectionHandle = this.metadata.handle || this.pageMeta?.dataset.collectionHandle || '';
    this.cacheVersionKey = this.metadata.cacheVersionKey || this.pageMeta?.dataset.cacheVersionKey || '';
    this.hydrationUrl = this.metadata.hydrationUrl || this.pageMeta?.dataset.hydrationUrl || '';
    this.indexEndpointBase = this.metadata.indexEndpointBase || this.pageMeta?.dataset.indexEndpointBase || '';
    this.indexUrlTemplate = this.metadata.indexUrlTemplate || this.pageMeta?.dataset.indexUrlTemplate || '';
    this.indexPageSize = toPositiveNumber(
      this.metadata.indexPageSize || this.pageMeta?.dataset.indexPageSize,
      COLLECTION_INDEX_PAGE_SIZE
    );
    this.expectedIndexPageCount = toPositiveNumber(
      this.metadata.expectedIndexPageCount || this.pageMeta?.dataset.expectedIndexPageCount,
      0
    );
    this.typeSeed = Array.isArray(this.metadata.typeSeed) ? this.metadata.typeSeed : [];
    this.storefrontApiToken = this.metadata.storefrontApi?.token || this.pageMeta?.dataset.storefrontApiToken || '';
    this.storefrontApiEndpoint = this.metadata.storefrontApi?.endpoint || this.pageMeta?.dataset.storefrontApiEndpoint || '';
    // Phase 1 (MMM render architecture adoption): SSR products and their IDs
    // are captured once at construction as locals; steady-state filter/result
    // state lives on `this.store` below. The `catalogProducts` Map (product
    // DATA, not filter state) remains a class field — it's not banned and
    // callers still consult it for compat matching and card rendering.
    const serverRenderedProducts = collectProducts(this.productsContainer || root);
    const initialServerRenderedIds = serverRenderedProducts
      .map((product) => product.numericId)
      .filter((id) => id > 0);
    this.initialSsrProductCount = serverRenderedProducts.length;

    this.catalogProducts = new Map();
    // Ordered catalog-of-record ID list. Populated by loadCatalogFromIndex().
    // Replaces the retired class-field catalog id list. Consumers treat it
    // as the authoritative order for "all products in the collection" prior
    // to any authority-result visibility trim.
    this.catalogIdOrder = [];
    this.hydrator = this.storefrontApiToken && this.storefrontApiEndpoint
      ? new HydrationService(
        new URL('./matchpoint-hydration-worker.js', import.meta.url).href,
        {
          token: this.storefrontApiToken,
          endpoint: this.storefrontApiEndpoint
        }
      )
      : null;
    this.indexService = this.collectionHandle && this.cacheVersionKey && this.indexUrlTemplate
      ? new CollectionIndexService({
        handle: this.collectionHandle,
        cacheVersionKey: this.cacheVersionKey,
        indexEndpointBase: this.indexEndpointBase,
        indexUrlTemplate: this.indexUrlTemplate,
        indexPageSize: this.indexPageSize,
        expectedIndexPageCount: this.expectedIndexPageCount
      })
      : null;
    this.filterEngine = new FilterEngine();
    this.sortEngine = new SortEngine();
    this.statsEngine = new StatsEngine();
    this.exactAuthority = this.indexService
      ? new CollectionExactAuthority({
        preferred: new CompactIndexExactAuthorityAdapter(this.indexService),
        fallback: new FullHydrationExactAuthorityAdapter({
          hydrator: this.hydrator,
          handle: this.collectionHandle,
          versionKey: this.cacheVersionKey,
          serverRenderedIds: initialServerRenderedIds,
          filterEngine: this.filterEngine,
          sortEngine: this.sortEngine,
          statsEngine: this.statsEngine
        })
      })
      : null;

    this.dropdownHierarchy = [];
    this.currentNextFilterKey = null;
    this.resolvedVehicleGidForSelection = null;
    this.compatibilityTouched = false;
    this.lastCompatibilityVehicleId = null;
    this.fallbackIcon = this.activeImage?.getAttribute('src') || getFallbackIcon();
    this.catalogHydrationPromise = null;
    this.catalogReady = false;
    this.compatibilityMatcherCache = new Map();
    this.compatibilityLabelCache = new Map();
    this.filterRunToken = 0;
    this.filterFlushTimer = null;
    this.filterFlushInFlight = false;
    this.pendingFilterRequest = null;
    this.catalogBackfillPromise = null;
    this.catalogBackfillTimer = null;
    this.catalogBackfillAttempt = 0;
    this.pageRenderToken = 0;
    this.typeOptionHintGeneration = 0;
    this.typeLabelCatalog = new Map(
      normalizeTypeOptionEntries(this.typeSeed).map((entry) => [entry.value, entry.label])
    );
    this.typeOptionsState = {
      source: 'none',
      precedence: TYPE_OPTION_SOURCE_PRECEDENCE.none,
      compatOnly: false,
      loadedPageCount: 0,
      expectedPageCount: 0,
      countsAreAuthoritative: false,
      optionCount: 0
    };
    this.isBlockingCatalogLoad = false;
    this.currentRenderContext = {
      compatibilityByProduct: null,
      vehicle: null,
      compatOnly: false,
      selectedType: '',
      resultSource: null,
      resultMode: null,
      quickPending: false
    };

    // =========================================================================
    // Phase 1 — Shared Collection Store (plan §Phase 1)
    //
    // MMM's filter/result state (`activeFilters`, `currentPage`,
    // `visibleProductIds`, `allProductIds`, `paginationItemCount`,
    // `totalProductCount`, `gridGeneration`) now lives on a shared-reducer
    // store instance per MMM page. Writes go through the singleton `bus` via
    // the same command factories the collection page already uses; reads go
    // through `this.store.getState()` and selectors.
    //
    // The Store subscribes to `bus.on('*')` in its constructor; the MMM page
    // does not load `matchpoint-collection.js`, so this Store is the sole
    // reducer consumer on the page and there is no cross-Store bleed.
    // =========================================================================
    this.store = new Store(collectionReducer, {
      ...createInitialState(null),
      currentPage: Number.isFinite(initialCurrentPage) && initialCurrentPage >= 1
        ? initialCurrentPage
        : 1,
      visibleProductIds: initialServerRenderedIds,
      allProductIds: initialServerRenderedIds,
      activeBaseProductIds: initialServerRenderedIds,
      totalProductCount: initialServerRenderedIds.length,
      paginationItemCount: initialServerRenderedIds.length,
      collection: {
        handle: this.collectionHandle || null,
        cacheVersionKey: this.cacheVersionKey || null
      }
    });

    // Phase 1 — subscription-driven renders. The shared Store pushes
    // `visibleProductIds` / `currentPage` / `paginationItemCount` changes
    // through here so the grid, paginator, and summary re-paint in reaction
    // to command-driven state mutations (e.g. authority-ready dispatch,
    // page-change dispatch) without requiring each write site to call the
    // renderers imperatively. Existing imperative callers remain for Phase 1
    // as a safety net; Phase 2 collapses them.
    // Phase 3 — grid no-op detection moved into `GridSignatureGate` (below).
    // Pagination still needs its own dedupe key since it has no gate of its own.
    this._lastRenderedPaginationKey = null;
    this._storeUnsubscribe = this.store.subscribe((state) => {
      this._onStoreStateChanged(state);
    });

    // =========================================================================
    // Phase 2 — shared PathCOrchestrator + HeadTailDispatcher (plan §Phase 2)
    //
    // The collection page's render architecture is constructed in
    // `matchpoint-collection.js`:
    //   - `new SortIndex(compactIndexStore)` provides per-sortKey ordered
    //     id arrays cached by (sortKey, storeVersion).
    //   - `new PathCOrchestrator({filterEngine, sortEngine, statsEngine,
    //     sortIndex})` produces head (first-pageSize), tail (authoritative
    //     filter+sort), and single-shot `evaluate()` payloads against the
    //     singleton compactIndexStore.
    //   - `new HeadTailDispatcher({orchestrator, store, triggerName})`
    //     coalesces user-trigger pulses into a single head→tail dispatch
    //     per rAF and emits `CMD_OPTIMISTIC_HEAD_READY` /
    //     `CMD_OPTIMISTIC_TAIL_READY` with head/tail prefix-identity
    //     guaranteed by shared sortIndex walk.
    //
    // MMM reuses the same singleton compactIndexStore (populated by this
    // page's CollectionIndexService) so the sortIndex / matcher caches
    // are shared across the collection page visit and the MMM visit
    // within a session. The only page-specific adjustment is:
    //   - Head pageSize = ITEMS_PER_PAGE (50) for MMM vs 48 for collection.
    //     Injected through an adapter because HeadTailDispatcher does
    //     NOT forward options into orchestrator.evaluateHead (it wants
    //     command-payload parity with the collection page).
    //   - Live activeFilters are composed from MMM's DOM
    //     (vehicle + compatibility toggle + product-type select) at
    //     orchestrator call time — MMM's filter UI is not a 1:1 match
    //     with the collection reducer's toggle-semantics
    //     CMD_FILTER_REQUEST, so the Store continues to own commit state
    //     (visibleProductIds / currentPage / gridGeneration) while DOM
    //     remains the source of truth for filter intent. This keeps the
    //     Store-as-authority invariant without shoehorning MMM into a
    //     filter-command shape that would have to change if the MMM
    //     filter UI ever evolves.
    // =========================================================================
    this.sortIndex = new SortIndex(compactIndexStore);
    this.pathCOrchestrator = new PathCOrchestrator({
      filterEngine: this.filterEngine,
      sortEngine: this.sortEngine,
      statsEngine: this.statsEngine,
      sortIndex: this.sortIndex
    });

    // Adapter wraps PathCOrchestrator so MMM's pageSize (50) is injected on
    // every head evaluate without forking the dispatcher. Live activeFilters
    // come from `_composeOrchestratorState` so the head/tail payload always
    // reflects the current DOM compat/type state even when the Store's
    // `activeFilters` slice hasn't been hydrated from a prior reducer commit.
    const orchestratorAdapter = {
      evaluateHead: (preOptimisticState) => {
        const liveState = this._composeOrchestratorState(preOptimisticState);
        return this.pathCOrchestrator.evaluateHead(liveState, {
          pageSize: ITEMS_PER_PAGE
        });
      },
      evaluateTail: (preOptimisticState, options) => {
        const liveState = this._composeOrchestratorState(preOptimisticState);
        return this.pathCOrchestrator.evaluateTail(liveState, options);
      }
    };

    this._headTailDispatcher = new HeadTailDispatcher({
      orchestrator: orchestratorAdapter,
      store: this.store,
      triggerName: 'mmm',
      debug
    });

    // =========================================================================
    // Phase 3 — shared grid signature gate (plan §Phase 3)
    //
    // MMM paints cards from `catalogProducts` into `productsContainer`. The
    // signature gate owns the "should this paint actually run?" decision,
    // replacing MMM's previous ad-hoc `_lastRenderedGridKey` string compare.
    //
    // Seed: if the Liquid template painted SSR cards, their IDs seed the
    // gate so the first Store-driven render that matches is a no-op. MMM
    // does not currently SSR product cards, so the seed is `::empty-ssr::`.
    // Either way, the first real paint has a unique signature and proceeds.
    //
    // Contract parity with collection page:
    //   - Signature format: `${currentPage}|${pageProductIds.join(',')}`.
    //   - `collection:grid:render-{complete,skipped,deferred}` events are
    //     dispatched on `document` so existing probes keyed on those names
    //     attribute MMM paints identically to collection paints.
    // =========================================================================
    this.signatureGate = new GridSignatureGate({
      gridContainer: this.productsContainer || null,
      itemSelector: '[data-attachment-product][data-product-id]',
      ssrPage: 1,
      debug
    });

    // Cold-boot progressive: every compactIndexStore upsert batch should
    // drive a fresh head/tail pair so the grid and facet counts climb as
    // index pages stream in. Guarded against post-authority drift by
    // checking `state.lifecycle.exactStateReady` — once the exact
    // authority has committed, further optimistic commits would overwrite
    // authoritative state. Mirrors the collection page's Phase 9 hook
    // (matchpoint-collection-effects-view.js §#handleCompactIndexStoreChange).
    this._onCompactIndexStoreChange = (evt) => {
      const state = this.store.getState();
      if (state?.lifecycle?.exactStateReady === true) return;

      // Progressive catalog seeding — mirrors the collection page's
      // ProductManager-before-Store seeding so the grid can paint the
      // first 48 cards as soon as compact-index pages arrive, rather
      // than waiting for `loadCatalogFromIndex()` to finish every page.
      //
      // Previously this handler bailed when `this.catalogProducts` was
      // empty, which serialized the entire cold-boot render behind full
      // catalog hydration. Now we mint lightweight MMM catalog entries
      // for every newly-upserted id directly from `compactIndexStore`,
      // so both `_onStoreStateChanged` (which gates on
      // `catalogProducts.size > 0`) and `renderResultsPage`'s
      // `this.catalogProducts.get(productId)` lookup succeed on the
      // first head commit. `loadCatalogFromIndex()` later rebuilds the
      // authoritative Map from the manifest + ordered page records,
      // which is a superset of what we seeded here.
      const upsertedIds = Array.isArray(evt?.detail?.upsertedIds)
        ? evt.detail.upsertedIds
        : [];
      if (upsertedIds.length && this.catalogProducts) {
        for (const rawId of upsertedIds) {
          const id = Number(rawId);
          if (!Number.isFinite(id) || id <= 0) continue;
          if (this.catalogProducts.has(id)) continue;
          const compactProduct = compactIndexStore.getById(id);
          if (!compactProduct) continue;
          const order = Number.isFinite(compactProduct.order)
            ? compactProduct.order
            : this.catalogIdOrder.length;
          this.catalogProducts.set(
            id,
            normalizeMachineMatchMakerCatalogProduct(compactProduct, order)
          );
          this.catalogIdOrder.push(id);
        }
      }

      if (!this.catalogProducts || this.catalogProducts.size === 0) return;
      // Streaming updates: only TAIL needs to re-emit. HEAD would regress
      // paginationItemCount to firstPageIds.length (and fc to null) for
      // ~10–15ms between TAIL commits, producing a visible "N → 50 → M"
      // count stutter on every compact-index page arrival (see
      // probe-mmm-count-writes). HEAD's value is a fast first paint on
      // user input; during progressive streaming the grid is already
      // painted and TAIL alone carries the authoritative updated list.
      this._headTailDispatcher.scheduleTailOnly('compact-index-store-change');
    };
    if (typeof document !== 'undefined') {
      document.addEventListener(
        COMPACT_INDEX_STORE_CHANGE_EVENT,
        this._onCompactIndexStoreChange
      );
    }

    // Debug-only: expose MMM internals on window.__MP_MMM__ when ?mp_debug=true.
    // Mirrors the `window.__MP` surface on the collection page so probes and
    // devtools can drive commands through `bus.dispatch(events.*(...))` and read
    // live state via `store.getState()` without relying on private class refs.
    try {
      if (typeof window !== 'undefined' &&
          /[?&]mp_debug=true\b/.test(window.location?.search || '')) {
        const mmmDebugSurface = {
          enabled: true,
          commitEvent: 'mp:commit',
          store: this.store,
          bus,
          wizard: this,
          // Phase 2 — expose the shared render-architecture instances so
          // probes can assert MMM is wired through the same dispatcher /
          // orchestrator the collection page already uses, rather than a
          // bespoke MMM evaluate path.
          pathCOrchestrator: this.pathCOrchestrator,
          sortIndex: this.sortIndex,
          headTailDispatcher: this._headTailDispatcher,
          compactIndexStore,
          // Phase 3 — expose the signature gate so probes can read
          // `getLastSignature()` and count `render-skipped` vs
          // `render-complete` emissions attributable to MMM paints.
          signatureGate: this.signatureGate,
          events: {
            filterRequestCommand,
            pageChangeRequestCommand,
            exactAuthorityReadyCommand,
            exactAuthorityQuickReadyCommand
          }
        };
        try {
          Object.defineProperty(window, '__MP_MMM__', {
            configurable: true,
            enumerable: false,
            writable: false,
            value: Object.freeze(mmmDebugSurface)
          });
        } catch (_) {
          window.__MP_MMM__ = mmmDebugSurface;
        }
      }
    } catch (_) {}

    debug.info('Constructed wizard instance', {
      currentPage: this.store.getState().currentPage,
      totalPages: this.totalPages,
      initialProductCount: this.initialSsrProductCount,
      hasVehicleApi: !!this.vehicleApi,
      collectionHandle: this.collectionHandle || null,
      hasHydrationService: !!this.hydrator,
      hasIndexService: !!this.indexService,
      expectedIndexPageCount: this.expectedIndexPageCount || null
    });
  }

  /**
   * Phase 1 — unified Store subscriber. Re-renders the grid and pagination
   * when their backing selectors change. Diff-gated with simple string keys
   * so identical commits (e.g. authority-ready where visibleProductIds was
   * already reference-stable via `coalesceIdArray`) short-circuit without a
   * render.
   *
   * @param {object} state
   */
  _onStoreStateChanged(state) {
    if (!state) return;
    const currentPage = Number.isFinite(state.currentPage) ? state.currentPage : 1;
    const visibleProductIds = Array.isArray(state.visibleProductIds)
      ? state.visibleProductIds
      : [];
    const paginationItemCount = Number.isFinite(state.paginationItemCount)
      ? state.paginationItemCount
      : visibleProductIds.length;

    // Guard against wiping SSR cards before the compact-index catalog has
    // hydrated `this.catalogProducts`. `renderResultsPage` rebuilds cards
    // from `this.catalogProducts.get(productId)`; with an empty Map every
    // lookup misses and `replaceChildren(emptyFragment)` would destroy the
    // Liquid-rendered SSR grid. Only trigger the grid render once there is
    // at least one card-data entry available.
    const catalogReadyForRender = this.catalogProducts && this.catalogProducts.size > 0;
    if (catalogReadyForRender) {
      // Cold-boot gate: the first store commit that has visible ids AND a
      // populated catalog is our first real paint — whether it came from
      // the HeadTailDispatcher (progressive head-commit as index pages
      // stream in) or the exact-authority path. Clear the gate here so
      // the list reveals as soon as the first card actually paints, and
      // subsequent store ticks keep climbing the count in place.
      if (visibleProductIds.length > 0) {
        document.documentElement.classList.remove('mmm-cold-boot-pending');
      }
      // Phase 3 — `GridSignatureGate` owns the "should we actually paint?"
      // decision. Fire into `renderResultsPage` on every store tick and
      // let the gate short-circuit identical slice+page signatures.
      // The gate emits `collection:grid:render-skipped` so probes observe
      // tail-commit no-ops without the caller having to predict them.
      Promise.resolve().then(() => this.renderResultsPage()).catch((error) => {
        debug.warn('MMM store-driven render failed.', error);
      });
    }

    // Pagination is safe to re-render any time — it only reads counts, not
    // card data.
    const paginationKey = `${currentPage}|${paginationItemCount}`;
    if (paginationKey !== this._lastRenderedPaginationKey) {
      this._lastRenderedPaginationKey = paginationKey;
      this.renderPagination(paginationItemCount);
    }

    // Progressive count — let the "Showing N attachments" summary climb on
    // every store tick so users see the number grow as compact-index pages
    // stream in during cold boot. Mirrors the collection page, where the
    // count badge updates on each optimistic head-commit. `renderSummary`
    // no-ops while the cold-boot class is still on <html> (keeps "Loading
    // all attachments..." visible until the first card paints), and the
    // authority-path applyAuthorityResult still emits its own renderSummary
    // for the final count with its effectiveSelectedType reconciliation.
    //
    // Reads `state.filteredProductCount` (NOT `paginationItemCount`). On a
    // HEAD commit the reducer sets `paginationItemCount = firstPageIds.length`
    // (a provisional value so the grid paints the first page) and
    // `filteredProductCount = null` (the authoritative count is not yet
    // known; TAIL carries it). Using `paginationItemCount` here would
    // render "Showing 50..." during HEAD and then "Showing N..." ~15ms
    // later when TAIL lands, producing a visible count-flash on every
    // filter-change and every streaming update. `filteredProductCount` is
    // the architectural field for "authoritative count" — when it's null
    // we skip the render entirely; the prior count remains on screen until
    // TAIL provides the new one. Identical-count cache also avoids
    // redundant textContent writes.
    const filteredProductCount = Number.isFinite(state.filteredProductCount)
      ? state.filteredProductCount
      : null;
    if (catalogReadyForRender
        && filteredProductCount != null
        && filteredProductCount !== this._lastRenderedSummaryCount) {
      const liveVehicle = this.vehicleApi?.activeVehicleDisplayData || null;
      const liveCompatOnly = !!this.compatibilityToggle?.checked && !!liveVehicle?.id;
      this._lastRenderedSummaryCount = filteredProductCount;
      this.renderSummary(
        filteredProductCount,
        liveVehicle,
        liveCompatOnly,
        this.getSelectedTypeValue()
      );
    }
  }

  async init() {
    debug.groupCollapsed('Init start');
    this.bindEvents();
    this.seedProductTypeOptions('init');

    const selectorInitialization = this.vehicleApi && this.form && this.fieldsContainer && this.submitButton
      ? this.initializeVehicleSelector()
      : Promise.resolve();

    try {
      await this.awaitVehicleReady();
      await selectorInitialization;
      await this.bootCatalog();
      await this.handleVehicleChange();
    } finally {
      // Cold-boot gate set inline in the Liquid template hides the SSR cards
      // while the vehicle-filtered render is in flight; clear it once init
      // settles (success or failure) so the list becomes visible.
      document.documentElement.classList.remove('mmm-cold-boot-pending');
      debug.groupEnd();
    }
  }

  async awaitVehicleReady() {
    const vehicleReady = this.vehicleApi?.vehicleReady;
    if (vehicleReady?.then) {
      await vehicleReady;
    }
  }

  bindEvents() {
    this.form?.addEventListener('submit', (event) => this.handleFormSubmit(event));
    this.productTypeFilter?.addEventListener('change', () => this.requestFilterApply({
      resetPage: true,
      reason: 'type-change',
      allowQuick: true
    }));
    this.compatibilityToggle?.addEventListener('change', () => {
      this.compatibilityTouched = true;
      this.requestFilterApply({
        resetPage: true,
        reason: 'compatibility-toggle',
        allowQuick: true
      });
    });
    this.pagination?.addEventListener('click', (event) => this.handlePaginationClick(event));

    this.openModalButton?.addEventListener('click', () => {
      if (typeof window.openVehicleModal === 'function') {
        window.openVehicleModal('attachment-wizard');
      }
    });

    if (this.vehicleApi) {
      document.addEventListener('activeVehicleChanged', () => {
        this.handleVehicleChange();
      });
    }

    if (this.indexService) {
      this.indexService.on(COLLECTION_INDEX_FACET_PARTIAL_EVENT, (payload = {}) => {
        this.handleFacetPartial(payload);
      });
    }
  }

  async bootCatalog() {
    if (!this.collectionHandle) {
      debug.warn('Collection handle missing; boot limited to server-rendered items.');
      return;
    }

    const activeVehicle = this.vehicleApi?.activeVehicleDisplayData || null;
    this.syncCompatibilityToggleState(activeVehicle);

    const cachedCollection = await warehouse.getCollection(this.collectionHandle);
    const versionMatches = warehouse.isCollectionVersionCurrent(cachedCollection, this.cacheVersionKey);
    const hasWarmIndexCoverage = versionMatches && cachedCollection?.indexComplete === true;
    const hasActiveVehicle = !!activeVehicle?.id;

    debug.groupCollapsed('Catalog boot path');
    debug.log('Boot state', {
      collectionHandle: this.collectionHandle,
      cacheVersionKey: this.cacheVersionKey,
      versionMatches,
      indexComplete: cachedCollection?.indexComplete ?? null,
      indexPageCount: cachedCollection?.indexPageCount ?? null,
      expectedIndexPageCount: this.expectedIndexPageCount || null,
      cardPayloadComplete: cachedCollection?.cardPayloadComplete ?? null,
      hasActiveVehicle,
      hasIndexService: !!this.indexService?.canFetch?.(),
      hasHydrationService: !!this.hydrator,
      initialServerProductCount: this.initialSsrProductCount
    });

    const loadedFromCache = await this.loadCatalogFromIndex(cachedCollection);
    if (loadedFromCache) {
      await this.applyFilters();
      emitCatalogSource('compact-index-cache', {
        collectionHandle: this.collectionHandle,
        version: this.cacheVersionKey,
        productCount: this.catalogIdOrder.length,
        cardPayloadComplete: cachedCollection?.cardPayloadComplete === true,
        manifestIndexComplete: cachedCollection?.indexComplete === true
      });
      debug.log('Fast path complete from shared compact-index cache.', {
        productCount: this.catalogIdOrder.length,
        note: 'MMM now trusts shared collectionIndexPages coverage instead of requiring full product payload hydration.'
      });
      debug.groupEnd();
      return;
    }

    if (hasWarmIndexCoverage) {
      debug.warn('Shared compact-index manifest is current, but paged index records were incomplete. Repairing from the shared compact-index endpoint.', {
        collectionHandle: this.collectionHandle,
        expectedProductCount: cachedCollection?.productCount || 0,
        loadedProductCount: this.catalogIdOrder.length
      });
    }

    if (this.indexService?.canFetch?.()) {
      if (hasActiveVehicle) {
        this.setBlockingCatalogState(true, 'Loading attachments for your machine...');
        await this.warmCatalogFromCompactIndex(true, hasWarmIndexCoverage ? 'compact-index-cache' : 'compact-index-network');
        debug.log('Foreground compact-index path complete.');
        debug.groupEnd();
        return;
      }

      this.warmCatalogFromCompactIndex(false, hasWarmIndexCoverage ? 'compact-index-cache' : 'compact-index-network').catch((error) => {
        debug.error('Background catalog acquisition failed; leaving server-rendered products in place.', error);
      });
      debug.log('Background compact-index warm started; SSR products remain visible until the shared index cache is ready.');
      debug.groupEnd();
      return;
    }

    if (hasActiveVehicle) {
      this.setBlockingCatalogState(true, 'Loading attachments for your machine...');
      await this.hydrateAndRenderCatalog();
      debug.log('Foreground fallback hydration path complete.');
      debug.groupEnd();
      return;
    }

    this.hydrateAndRenderCatalog(false).catch((error) => {
      debug.error('Background fallback hydration failed; leaving server-rendered products in place.', error);
    });
    debug.log('Background fallback hydration started; SSR products remain visible until catalog render completes.');
    debug.groupEnd();
  }

  async loadCatalogFromIndex(collectionRecord = null) {
    const manifest = collectionRecord || await warehouse.getCollection(this.collectionHandle);
    const resolvedPageCount = toPositiveNumber(
      manifest?.indexPageCount,
      this.expectedIndexPageCount
    );
    const resolvedProductCount = Number(manifest?.productCount || 0);

    if (manifest?.indexComplete === true && resolvedProductCount === 0) {
      this.catalogProducts = new Map();
      this.catalogIdOrder = [];
      this.catalogReady = true;
      this.compatibilityLabelCache.clear();
      recordCatalogCacheProbe({
        source: 'collectionIndexPages',
        status: 'empty-success',
        manifestIndexComplete: manifest?.indexComplete === true,
        resolvedPageCount,
        resolvedProductCount
      });
      return true;
    }

    const pageNumbers = buildPageNumberRange(resolvedPageCount);
    if (!pageNumbers.length) {
      this.catalogProducts = new Map();
      this.catalogIdOrder = [];
      this.catalogReady = false;
      recordCatalogCacheProbe({
        source: 'collectionIndexPages',
        status: 'failed',
        reason: 'no-page-count',
        manifestIndexComplete: manifest?.indexComplete === true,
        resolvedPageCount,
        resolvedProductCount
      });
      return false;
    }

    const pageRecords = await warehouse.getCollectionIndexPages(
      this.collectionHandle,
      this.cacheVersionKey,
      pageNumbers
    );

    if (pageRecords.size !== pageNumbers.length) {
      this.catalogProducts = new Map();
      this.catalogIdOrder = [];
      this.catalogReady = false;
      recordCatalogCacheProbe({
        source: 'collectionIndexPages',
        status: 'failed',
        reason: 'page-miss',
        manifestIndexComplete: manifest?.indexComplete === true,
        resolvedPageCount,
        resolvedProductCount,
        pageHits: pageRecords.size,
        pageMisses: pageNumbers.length - pageRecords.size
      });
      return false;
    }

    const catalog = buildMachineMatchMakerCatalogFromIndexPages({
      pageRecordsByNumber: pageRecords,
      manifest,
      expectedPageCount: this.expectedIndexPageCount,
      fallbackPageSize: this.indexPageSize
    });

    this.catalogProducts = catalog.productsById;
    this.catalogIdOrder = Array.isArray(catalog.orderedProductIds)
      ? catalog.orderedProductIds
      : [];
    this.catalogReady = catalog.catalogReady;
    this.compatibilityLabelCache.clear();

    recordCatalogCacheProbe({
      source: 'collectionIndexPages',
      status: this.catalogReady ? 'success' : 'failed',
      reason: this.catalogReady ? null : 'empty-catalog',
      manifestIndexComplete: manifest?.indexComplete === true,
      resolvedPageCount,
      resolvedProductCount,
      manifestProductIdCount: catalog.manifestProductIdCount,
      loadedProductCount: catalog.loadedProductCount,
      orderedProductCount: catalog.orderedProductCount
    });

    debug.info('Loaded catalog from shared compact-index cache', {
      collectionHandle: this.collectionHandle,
      productCount: catalog.orderedProductCount,
      uniqueProductCount: catalog.loadedProductCount,
      expectedProductCount: resolvedProductCount,
      pageCount: resolvedPageCount
    });

    return this.catalogReady;
  }

  async warmCatalogFromCompactIndex(blocking = true, sourceOverride = 'compact-index-network') {
    if (this.catalogHydrationPromise) {
      return this.catalogHydrationPromise;
    }

    const run = (async () => {
      debug.groupCollapsed('Compact-index catalog acquisition start');
      debug.log('Compact-index path', {
        blocking,
        collectionHandle: this.collectionHandle,
        expectedIndexPageCount: this.expectedIndexPageCount || null,
        indexPageSize: this.indexPageSize
      });

      if (blocking) {
        this.setBlockingCatalogState(true, STATUS_TEXT.LOADING);
      }

      await this.primeFacetHintSnapshot('catalog-boot');

      const coverage = await this.indexService.warmAllPages({
        reason: 'machine-match-maker-boot'
      });
      const loadedFromIndex = await this.loadCatalogFromIndex();

      if (!loadedFromIndex) {
        throw new Error('Compact-index warm succeeded, but MMM could not rebuild a full catalog from collectionIndexPages.');
      }

      await this.applyFilters({ reason: 'catalog-boot' });
      emitCatalogSource(sourceOverride, {
        collectionHandle: this.collectionHandle,
        version: this.cacheVersionKey,
        productCount: this.catalogIdOrder.length,
        loadedPages: coverage?.loadedPages ?? null,
        pageCount: coverage?.pageCount ?? this.expectedIndexPageCount ?? null
      });
      debug.groupEnd();
    })()
      .catch(async (error) => {
        if (error instanceof EvaluationAbortedError || error?.name === 'EvaluationAbortedError') {
          debug.debug('Compact-index warm render was superseded by a newer MMM filter generation; preserving compact-index boot source.', {
            sourceOverride,
            generation: this.filterRunToken,
            phase: error?.phase || null
          });
          if (this.catalogReady) {
            await this.applyFilters({ reason: 'catalog-boot-retry' });
            emitCatalogSource(sourceOverride, {
              collectionHandle: this.collectionHandle,
              version: this.cacheVersionKey,
              productCount: this.catalogIdOrder.length,
              loadedPages: this.expectedIndexPageCount || null,
              pageCount: this.expectedIndexPageCount || null,
              supersededRender: true
            });
          }
          return;
        }
        debug.warn('Compact-index acquisition failed; falling back to full hydration.', error);
        return this.hydrateAndRenderCatalog(blocking, error, true);
      })
      .finally(() => {
        this.catalogHydrationPromise = null;
        if (blocking) {
          this.setBlockingCatalogState(false);
        }
      });

    this.catalogHydrationPromise = run;
    return run;
  }

  async hydrateAndRenderCatalog(blocking = true, compactIndexError = null, fromCompactIndexFailure = false) {
    if (this.catalogHydrationPromise && !fromCompactIndexFailure) {
      return this.catalogHydrationPromise;
    }

    // Re-derive SSR product IDs from the current DOM for the hydration
    // fallback. Prior to Phase 1 this read a cached DOM snapshot; with that
    // class field eliminated we re-collect at the point of use. Cheap DOM
    // traversal and no stored staleness.
    const hydrationSeedProducts = collectProducts(this.productsContainer || this.root);
    const serverRenderedIds = hydrationSeedProducts
      .map((product) => product.numericId)
      .filter((id) => id > 0);

    const run = (async () => {
      debug.groupCollapsed('Catalog hydration start');
      debug.log('Hydration path', {
        blocking,
        serverRenderedIds: serverRenderedIds.length,
        via: this.hydrator ? 'HydrationService' : 'unavailable',
        compactIndexFailure: compactIndexError?.message || null
      });

      if (blocking) {
        this.setBlockingCatalogState(true, STATUS_TEXT.LOADING);
      }

      if (!this.hydrator) {
        throw new Error('Machine Match Maker full-hydration fallback requires Storefront API configuration.');
      }

      await this.hydrator.fetchAndCacheAll(
        this.collectionHandle,
        this.cacheVersionKey,
        serverRenderedIds
      );

      const loadedFromIndex = await this.loadCatalogFromIndex();
      if (!loadedFromIndex) {
        throw new Error('Full hydration fallback completed, but MMM could not rebuild the catalog from collectionIndexPages.');
      }

      await this.applyFilters({ reason: 'catalog-boot' });
      emitCatalogSource('full-hydration-fallback', {
        collectionHandle: this.collectionHandle,
        version: this.cacheVersionKey,
        productCount: this.catalogIdOrder.length,
        via: 'HydrationService',
        compactIndexFailure: compactIndexError?.message || null
      });
      this.scheduleCompactIndexBackfill({
        reason: 'catalog-full-hydration-fallback',
        compactIndexFailure: compactIndexError?.message || null,
        resetAttempt: true
      });
      debug.groupEnd();
    })()
      .catch((error) => {
        this.catalogReady = false;
        throw error;
      })
      .finally(() => {
        if (blocking) {
          this.setBlockingCatalogState(false);
        }
      });

    if (!fromCompactIndexFailure) {
      this.catalogHydrationPromise = run;
    }
    return run;
  }

  scheduleCompactIndexBackfill({
    reason = 'full-hydration-fallback',
    compactIndexFailure = null,
    resetAttempt = false
  } = {}) {
    if (!this.indexService?.canFetch?.()) {
      return null;
    }

    if (resetAttempt) {
      this.catalogBackfillAttempt = 0;
    }

    if (this.catalogBackfillPromise || this.catalogBackfillTimer) {
      recordBackfillProbe({
        status: 'deduped',
        reason,
        attempt: this.catalogBackfillAttempt || 0,
        compactIndexFailure
      });
      return this.catalogBackfillPromise;
    }

    const nextAttempt = this.catalogBackfillAttempt + 1;
    const retryDelayMs = MMM_BACKFILL_RETRY_DELAYS_MS[Math.min(
      Math.max(nextAttempt - 1, 0),
      MMM_BACKFILL_RETRY_DELAYS_MS.length - 1
    )];

    recordBackfillProbe({
      status: 'scheduled',
      reason,
      attempt: nextAttempt,
      retryDelayMs,
      compactIndexFailure
    });

    this.catalogBackfillTimer = window.setTimeout(() => {
      this.catalogBackfillTimer = null;
      this.catalogBackfillPromise = this.runCompactIndexBackfill({
        reason,
        compactIndexFailure
      })
        .catch(() => {})
        .finally(() => {
          this.catalogBackfillPromise = null;
        });
    }, retryDelayMs);

    return this.catalogBackfillPromise;
  }

  async runCompactIndexBackfill({
    reason = 'full-hydration-fallback',
    compactIndexFailure = null
  } = {}) {
    const attempt = this.catalogBackfillAttempt + 1;
    this.catalogBackfillAttempt = attempt;

    recordBackfillProbe({
      status: 'started',
      reason,
      attempt,
      compactIndexFailure
    });

    try {
      let manifest = await warehouse.getCollection(this.collectionHandle);
      let pageNumbers = buildPageNumberRange(
        toPositiveNumber(manifest?.indexPageCount, this.expectedIndexPageCount)
      );

      if (!pageNumbers.length) {
        await this.indexService.warmAllPages({
          reason: 'machine-match-maker-backfill-bootstrap'
        });
        manifest = await warehouse.getCollection(this.collectionHandle);
        pageNumbers = buildPageNumberRange(
          toPositiveNumber(manifest?.indexPageCount, this.expectedIndexPageCount)
        );
      }

      if (!pageNumbers.length) {
        throw new Error('Machine Match Maker compact-index backfill could not resolve any page numbers.');
      }

      const pageProbe = await this.indexService.fetchFreshnessProbe(pageNumbers, {
        reason: 'machine-match-maker-backfill'
      });
      const pagePayloads = pageNumbers.map((page) => ({
        page,
        payload: pageProbe.get(page)?._payload || null
      })).filter((entry) => entry.payload);

      if (pagePayloads.length !== pageNumbers.length) {
        throw new Error(`Machine Match Maker compact-index backfill resolved ${pagePayloads.length}/${pageNumbers.length} authoritative pages.`);
      }

      await this.indexService.invalidateAndPersistPages(pagePayloads);
      manifest = await warehouse.getCollection(this.collectionHandle);
      const indexPageHashCount = Object.keys(manifest?.indexPageHashes || {}).length;

      recordBackfillProbe({
        status: 'success',
        reason,
        attempt,
        compactIndexFailure,
        loadedPages: pagePayloads.length,
        pageCount: manifest?.indexPageCount ?? this.expectedIndexPageCount ?? null,
        manifestRefreshedAt: manifest?.manifestRefreshedAt || null,
        indexPageHashCount
      });
      return manifest;
    } catch (error) {
      const shouldRetry = attempt < MMM_BACKFILL_RETRY_DELAYS_MS.length;

      recordBackfillProbe({
        status: shouldRetry ? 'retrying' : 'failed',
        reason,
        attempt,
        compactIndexFailure,
        error: error?.message || String(error),
        nextRetryDelayMs: shouldRetry
          ? MMM_BACKFILL_RETRY_DELAYS_MS[Math.min(attempt, MMM_BACKFILL_RETRY_DELAYS_MS.length - 1)]
          : null
      });

      if (shouldRetry) {
        this.catalogBackfillPromise = null;
        this.scheduleCompactIndexBackfill({
          reason,
          compactIndexFailure
        });
      }

      throw error;
    }
  }

  setBlockingCatalogState(isBlocking, summaryText = '') {
    this.isBlockingCatalogLoad = isBlocking;

    if (this.productsContainer) {
      this.productsContainer.hidden = isBlocking;
      this.productsContainer.toggleAttribute('aria-busy', isBlocking);
    }

    if (this.pagination) {
      if (isBlocking) {
        this.pagination.setAttribute('hidden', '');
      } else if (!this.catalogReady) {
        this.pagination.removeAttribute('hidden');
      }
    }

    if (isBlocking && this.summary && summaryText) {
      this.summary.textContent = summaryText;
    }
  }

  async initializeVehicleSelector() {
    if (!this.vehicleApi || !this.fieldsContainer || !this.submitButton) return;

    this.clearDropdownsFrom(0);
    this.fieldsContainer.innerHTML = '';
    this.dropdownHierarchy = [];
    this.resolvedVehicleGidForSelection = null;
    this.submitButton.disabled = true;

    const { pathPrefix, nextFieldKey } = computePathAndNext({});
    const data = await getFiltersCached(this.vehicleApi.fetchAppProxy, pathPrefix);
    prefetchChildren(this.vehicleApi.fetchAppProxy, pathPrefix, nextFieldKey, data.options);

    this.currentNextFilterKey = nextFieldKey;
    if (data.options?.length && nextFieldKey) {
      this.addDropdown(nextFieldKey, data.options);
    }
  }

  clearDropdownsFrom(index) {
    for (let i = index; i < this.dropdownHierarchy.length; i += 1) {
      this.dropdownHierarchy[i]?.field?.remove();
    }
    this.dropdownHierarchy.splice(index);
    this.resolvedVehicleGidForSelection = null;
    this.submitButton.disabled = true;
  }

  addDropdown(fieldKey, options) {
    const field = document.createElement('label');
    field.className = 'attachment-wizard__field';

    const label = document.createElement('span');
    label.className = 'attachment-wizard__field-label';
    label.textContent = fieldKey
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());

    const select = document.createElement('select');
    select.className = 'attachment-wizard__select';
    select.name = fieldKey;
    select.required = true;

    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = `Select ${label.textContent}`;
    select.appendChild(placeholderOption);

    options.forEach((option) => {
      const optionElement = document.createElement('option');
      optionElement.value = option;
      optionElement.textContent = option;
      select.appendChild(optionElement);
    });

    select.addEventListener('change', async () => {
      const currentIndex = this.dropdownHierarchy.findIndex((dropdown) => dropdown.key === fieldKey);
      this.clearDropdownsFrom(currentIndex + 1);

      if (select.value) {
        await this.fetchNextDropdownData();
      }
    });

    field.append(label, select);
    this.fieldsContainer.appendChild(field);
    this.dropdownHierarchy.push({ key: fieldKey, field, select });
  }

  async fetchNextDropdownData() {
    const filters = {};
    this.dropdownHierarchy.forEach((dropdown) => {
      if (dropdown.select.value) {
        filters[dropdown.key] = dropdown.select.value;
      }
    });

    const { pathPrefix, nextFieldKey } = computePathAndNext(filters);

    this.submitButton.disabled = true;
    this.resolvedVehicleGidForSelection = null;

    const data = await getFiltersCached(this.vehicleApi.fetchAppProxy, pathPrefix);
    prefetchChildren(this.vehicleApi.fetchAppProxy, pathPrefix, nextFieldKey, data.options);
    this.currentNextFilterKey = nextFieldKey;

    if (!nextFieldKey) {
      const vehicleRes = await this.vehicleApi.fetchAppProxy('vehicle', { path: pathPrefix });
      this.resolvedVehicleGidForSelection = vehicleRes?.vehicle_gid || null;
      this.submitButton.disabled = !this.resolvedVehicleGidForSelection;
      return;
    }

    if (data.options?.length) {
      this.addDropdown(nextFieldKey, data.options);
    }
  }

  async handleFormSubmit(event) {
    event.preventDefault();

    if (!this.vehicleApi || !this.submitButton || !this.resolvedVehicleGidForSelection) return;

    const originalLabel = this.submitButton.textContent;
    this.submitButton.disabled = true;
    this.submitButton.textContent = 'Adding...';

    try {
      const vehicleDetails = await this.vehicleApi.fetchVehicleDetailsById(this.resolvedVehicleGidForSelection);
      if (!vehicleDetails?.id) {
        throw new Error('Vehicle details were not returned.');
      }

      this.updateVehicleInLocalStorage(vehicleDetails);
      await this.vehicleApi.setActiveVehicleInCart(vehicleDetails.id, vehicleDetails);
      await this.initializeVehicleSelector();
    } catch (error) {
      console.error('[Machine Match Maker] Failed to add machine.', error);
    } finally {
      this.submitButton.disabled = !this.resolvedVehicleGidForSelection;
      this.submitButton.textContent = originalLabel;
    }
  }

  updateVehicleInLocalStorage(vehicleDetails) {
    // Fallback is site-scoped so a load-order race cannot silently write to
    // an unrelated key or cross-shop contaminate on shared origins.
    const storageKey = this.vehicleApi.LOCAL_STORAGE_SAVED_VEHICLES_KEY
      || `user_saved_vehicles:${(window.Shopify && window.Shopify.shop) || window.location.hostname}`;
    let savedVehicles = [];

    try {
      savedVehicles = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (_) {
      savedVehicles = [];
    }

    const nextVehicle = {
      id: vehicleDetails.id,
      displayName: vehicleDetails.displayName,
      thumbnail_url: vehicleDetails.thumbnail_url || null,
      active: true
    };

    const merged = savedVehicles
      .filter((vehicle) => vehicle?.id !== vehicleDetails.id)
      .map((vehicle) => ({ ...vehicle, active: false }));

    merged.unshift(nextVehicle);
    localStorage.setItem(storageKey, JSON.stringify(merged));
  }

  async handleVehicleChange() {
    const vehicle = this.vehicleApi?.activeVehicleDisplayData || null;
    const previousVehicleId = this.lastCompatibilityVehicleId;
    this.renderActiveVehicle(vehicle);

    if (!this.compatibilityToggle) {
      await this.applyFilters({
        resetPage: !!previousVehicleId,
        reason: 'vehicle-change',
        allowQuick: !!vehicle?.id || !!previousVehicleId
      });
      return;
    }

    this.syncCompatibilityToggleState(vehicle);

    if (vehicle?.id && !this.catalogReady && this.catalogHydrationPromise) {
      this.setBlockingCatalogState(true, STATUS_TEXT.LOADING);
      try {
        await this.catalogHydrationPromise;
      } finally {
        this.setBlockingCatalogState(false);
      }
    }

    // Phase 1 fallback: catalog hydration still in flight and no vehicle yet.
    // Re-collect SSR product IDs from the DOM and push them through the store
    // via a quick authority-ready dispatch so subscribers (grid/paginator)
    // re-paint from a single source of truth. This replaces the pre-Phase-1
    // pattern of mutating the class-field visible slice directly.
    const pendingSsrProducts = !this.catalogReady && this.catalogHydrationPromise
      ? collectProducts(this.productsContainer || this.root)
      : [];
    const pendingSsrIds = pendingSsrProducts
      .map((product) => product.numericId)
      .filter((id) => id > 0);
    if (!vehicle?.id && pendingSsrIds.length) {
      this.currentRenderContext = {
        compatibilityByProduct: null,
        vehicle,
        compatOnly: false,
        selectedType: this.getSelectedTypeValue(),
        resultSource: 'ssr',
        resultMode: 'ssr-pending',
        quickPending: false
      };
      // Reset pagination state on the store before publishing the SSR slice.
      bus.dispatch(pageChangeRequestCommand(1));
      bus.dispatch(exactAuthorityQuickReadyCommand({
        visibleProductIds: pendingSsrIds,
        totalProductCount: pendingSsrIds.length,
        paginationItemCount: pendingSsrIds.length,
        activeBaseCount: pendingSsrIds.length,
        generation: this.store.getState().gridGeneration || 0,
        source: 'mmm-ssr-fallback',
        resolvedAt: Date.now()
      }));
      // Subscriber re-renders grid + pagination; summary is not store-driven
      // in Phase 1 so we still fire it imperatively.
      this.renderSummary(pendingSsrIds.length, vehicle, false, this.getSelectedTypeValue());
      return;
    }

    await this.applyFilters({
      resetPage: !!vehicle?.id || !!previousVehicleId,
      reason: 'vehicle-change',
      allowQuick: !!vehicle?.id || !!previousVehicleId
    });
  }

  async handlePaginationClick(event) {
    const link = event.target.closest('[data-attachment-wizard-page]');
    if (!link) return;

    event.preventDefault();

    const nextPage = Number(link.dataset.attachmentWizardPage);
    const currentPage = this.store.getState().currentPage;
    if (!Number.isFinite(nextPage) || nextPage === currentPage) {
      return;
    }

    // Phase 1 — route page changes through the shared reducer. The store
    // subscriber will re-render the grid and pagination; catalog-not-ready
    // paths still fall through to applyFilters so the hydration + evaluate
    // cycle can publish an authority-ready commit.
    bus.dispatch(pageChangeRequestCommand(nextPage));

    if (!this.catalogReady) {
      await this.applyFilters({ reason: 'catalog-boot' });
      return;
    }

    // Grid + pagination renders are now subscriber-driven via
    // `_onStoreStateChanged`. No imperative render calls required here.
  }

  renderActiveVehicle(vehicle) {
    const label = vehicle?.id ? 'Active machine' : 'No machine selected';
    const name = vehicle?.displayName || 'Select a machine to show compatible attachments.';
    const copy = vehicle?.id
      ? 'Compatibility results below are now tied to this machine.'
      : 'Compatible results will appear automatically after a machine is selected.';
    const image = vehicle?.thumbnail_url && vehicle.thumbnail_url.startsWith('http')
      ? vehicle.thumbnail_url
      : this.fallbackIcon;

    if (this.activeImage) {
      this.activeImage.src = image;
      this.activeImage.onerror = () => {
        this.activeImage.src = this.fallbackIcon;
        this.activeImage.onerror = null;
      };
    }

    if (this.activeLabel) this.activeLabel.textContent = label;
    if (this.activeName) this.activeName.textContent = name;
    if (this.activeCopy) this.activeCopy.textContent = copy;
  }

  getCompatibilityMatcher(vehicleId) {
    if (!vehicleId) {
      return null;
    }

    if (!this.compatibilityMatcherCache.has(vehicleId)) {
      try {
        const matcher = createCompatibilityMatcher([vehicleId]);
        this.compatibilityMatcherCache.set(vehicleId, matcher);
      } catch (error) {
        this.compatibilityMatcherCache.delete(vehicleId);
        throw error;
      }
    }

    return this.compatibilityMatcherCache.get(vehicleId);
  }

  async getPageCompatibilityMap(pageProductIds = [], vehicleId) {
    if (!vehicleId) {
      return null;
    }

    const compatibilityMatcher = await this.getCompatibilityMatcher(vehicleId);
    const vehicleCache = this.compatibilityLabelCache.get(vehicleId) || new Map();
    const compatibilityByProduct = new Map();

    if (!compatibilityMatcher) {
      return compatibilityByProduct;
    }

    pageProductIds.forEach((productId) => {
      if (!vehicleCache.has(productId)) {
        const product = this.catalogProducts.get(productId);
        vehicleCache.set(productId, this.getProductCompatibility(product, compatibilityMatcher));
      }
      compatibilityByProduct.set(productId, vehicleCache.get(productId) === true);
    });

    this.compatibilityLabelCache.set(vehicleId, vehicleCache);
    return compatibilityByProduct;
  }

  getProductCompatibility(product, compatibilityMatcher) {
    if (!compatibilityMatcher || !product?.variants?.length) {
      return false;
    }

    return product.variants.some((variant) => compatibilityMatcher({
      bloom: variant.bloom || '',
      universal: String(variant.universal ?? '')
    }));
  }

  setStatus(text) {
    if (this.summary) {
      this.summary.textContent = text;
    }
  }

  getSelectedTypeValue() {
    return normalize(this.productTypeFilter?.value);
  }

  getSelectedTypeLabel(selectedTypeValue = this.getSelectedTypeValue()) {
    if (!selectedTypeValue || !this.productTypeFilter) {
      return '';
    }

    const selectedOption = Array.from(this.productTypeFilter.options || [])
      .find((option) => normalize(option.value) === selectedTypeValue);
    const label = selectedOption?.dataset?.typeLabel || selectedOption?.textContent || '';
    return (label || '').trim();
  }

  buildActiveFilters({ vehicleId = null, compatOnly = false, selectedTypeLabel = '' } = {}) {
    return {
      compatibility: {
        active: compatOnly && !!vehicleId,
        value: compatOnly && vehicleId ? vehicleId : null
      },
      price: {
        active: false,
        value: {
          min: 0,
          max: 0
        }
      },
      delivery: {
        active: false,
        value: {
          maxDays: 30
        }
      },
      productType: selectedTypeLabel ? new Set([selectedTypeLabel]) : new Set(),
      list: new Map(),
      custom: new Map()
    };
  }

  seedProductTypeOptions(reason = 'boot-seed') {
    return this.syncProductTypeOptions({
      productTypes: this.typeSeed,
      selectedType: this.getSelectedTypeValue(),
      source: 'type-seed',
      compatOnly: false,
      countsAreAuthoritative: false,
      reason
    });
  }

  syncCompatibilityToggleState(vehicle = null) {
    if (!this.compatibilityToggle) {
      return false;
    }

    if (vehicle?.id) {
      const vehicleChanged = this.lastCompatibilityVehicleId !== vehicle.id;
      this.compatibilityToggle.disabled = false;
      this.compatibilityToggle.closest('.attachment-wizard__checkbox')?.classList.remove('is-disabled');
      if (!this.compatibilityTouched || vehicleChanged) {
        this.compatibilityToggle.checked = true;
      }
      this.lastCompatibilityVehicleId = vehicle.id;
    } else {
      this.compatibilityToggle.checked = false;
      this.compatibilityToggle.disabled = true;
      this.compatibilityToggle.closest('.attachment-wizard__checkbox')?.classList.add('is-disabled');
      this.compatibilityTouched = false;
      this.lastCompatibilityVehicleId = null;
    }

    return !!vehicle?.id && !!this.compatibilityToggle.checked;
  }

  syncProductTypeOptions({
    productTypes = [],
    selectedType = '',
    source = 'facet-state',
    compatOnly = false,
    countsAreAuthoritative = false,
    loadedPageCount = 0,
    expectedPageCount = 0,
    reason = 'view-update'
  } = {}) {
    if (!this.productTypeFilter) {
      return selectedType;
    }

    const normalizedEntries = normalizeTypeOptionEntries(productTypes);
    const labelCatalog = this.typeLabelCatalog instanceof Map
      ? this.typeLabelCatalog
      : new Map();
    const canonicalEntries = normalizedEntries.map((entry) => {
      const canonicalLabel = labelCatalog.get(entry.value) || entry.label;
      if (!labelCatalog.has(entry.value)) {
        labelCatalog.set(entry.value, canonicalLabel);
      }
      return {
        ...entry,
        label: canonicalLabel
      };
    });
    this.typeLabelCatalog = labelCatalog;
    const currentState = this.typeOptionsState || {
      source: 'none',
      precedence: TYPE_OPTION_SOURCE_PRECEDENCE.none,
      loadedPageCount: 0
    };
    const nextPrecedence = TYPE_OPTION_SOURCE_PRECEDENCE[source] || TYPE_OPTION_SOURCE_PRECEDENCE.none;
    const nextLoadedPageCount = Number(loadedPageCount) || 0;
    const nextExpectedPageCount = Number(expectedPageCount) || 0;

    if (currentState.precedence > nextPrecedence) {
      return selectedType;
    }

    if (
      currentState.precedence === nextPrecedence &&
      source === 'facet-partial' &&
      nextLoadedPageCount > 0 &&
      nextLoadedPageCount < Number(currentState.loadedPageCount || 0)
    ) {
      return selectedType;
    }

    const allOption = document.createElement('option');
    allOption.value = '';
    allOption.textContent = 'All product types';

    this.productTypeFilter.replaceChildren(allOption);

    canonicalEntries.forEach((productType) => {
      const option = document.createElement('option');
      option.value = productType.value;
      option.textContent = productType.label;
      option.dataset.typeLabel = productType.label;
      this.productTypeFilter.appendChild(option);
    });

    const availableValues = new Set(canonicalEntries.map((productType) => productType.value));
    const nextSelectedType = selectedType && availableValues.has(selectedType) ? selectedType : '';
    this.productTypeFilter.value = nextSelectedType;
    this.typeOptionsState = {
      source,
      precedence: nextPrecedence,
      compatOnly,
      loadedPageCount: nextLoadedPageCount,
      expectedPageCount: nextExpectedPageCount,
      countsAreAuthoritative: countsAreAuthoritative === true,
      optionCount: canonicalEntries.length
    };

    recordTypeOptionsProbe({
      source,
      reason,
      compatOnly,
      selectedType: nextSelectedType,
      previousSelectedType: selectedType,
      selectionReset: !!selectedType && nextSelectedType !== selectedType,
      optionCount: canonicalEntries.length,
      loadedPageCount: nextLoadedPageCount,
      expectedPageCount: nextExpectedPageCount,
      countsAreAuthoritative: countsAreAuthoritative === true,
      options: canonicalEntries.map((entry) => ({
        value: entry.value,
        label: entry.label
      }))
    });

    return nextSelectedType;
  }

  syncProductTypeOptionsFromFacetState(facetState = {}, selectedType, { compatOnly = false, reason = 'view-update' } = {}) {
    const productTypes = Array.isArray(facetState?.productTypes) ? facetState.productTypes : [];
    return this.syncProductTypeOptions({
      productTypes,
      selectedType,
      source: 'facet-state',
      compatOnly,
      countsAreAuthoritative: true,
      reason
    });
  }

  handleFacetPartial(payload = {}) {
    const partialFacetState = payload?.facetStatePartial;
    if (!partialFacetState || !this.productTypeFilter) {
      return;
    }

    const vehicleId = this.vehicleApi?.activeVehicleDisplayData?.id || null;
    const compatOnly = !!vehicleId && !!this.compatibilityToggle?.checked;
    this.syncProductTypeOptions({
      productTypes: partialFacetState.productTypes,
      selectedType: this.getSelectedTypeValue(),
      source: 'facet-partial',
      compatOnly,
      countsAreAuthoritative: Array.isArray(partialFacetState.productTypes) &&
        partialFacetState.productTypes.every((entry) => Number.isFinite(entry?.count)),
      loadedPageCount: partialFacetState.loadedPageCount ?? payload.loadedPageCount ?? 0,
      expectedPageCount: partialFacetState.expectedPageCount ?? payload.expectedPageCount ?? 0,
      reason: 'facet-partial'
    });
  }

  async primeFacetHintSnapshot(reason = 'catalog-boot') {
    if (!this.indexService) {
      return null;
    }

    const vehicleId = this.vehicleApi?.activeVehicleDisplayData?.id || null;
    const compatOnly = !!vehicleId && !!this.compatibilityToggle?.checked;
    const selectedTypeValue = this.getSelectedTypeValue();
    const authorityState = this.getAuthorityState({
      vehicleId,
      compatOnly,
      selectedTypeValue
    });
    const generation = ++this.typeOptionHintGeneration;

    try {
      return await this.indexService.setFacetHintSnapshot({
        activeFilters: authorityState.activeFilters,
        sortKey: authorityState.sortKey,
        vehicleGid: vehicleId,
        generation
      });
    } catch (error) {
      debug.warn('Failed to arm MMM facet hint snapshot.', {
        generation,
        reason,
        error: error?.message || String(error)
      });
      return null;
    }
  }

  getAuthorityState({ vehicleId = null, compatOnly = false, selectedTypeValue = '' } = {}) {
    return {
      activeFilters: this.buildActiveFilters({
        vehicleId,
        compatOnly,
        selectedTypeLabel: this.getSelectedTypeLabel(selectedTypeValue)
      }),
      sortKey: 'best-selling',
      totalProductCount: this.catalogIdOrder.length
    };
  }

  /**
   * Phase 2 — assemble a reducer-shaped state snapshot for the orchestrator.
   * Starts from the live Store state (so `currentPage`, `gridGeneration`,
   * `visibleProductIds`, and `sortKey` remain authoritative) and overlays
   * DOM-derived `activeFilters`. The orchestrator only reads
   * `state.activeFilters` and `state.sortKey`; the rest of the snapshot is
   * passed through untouched to preserve reducer contracts.
   *
   * Phase 4 — `sortKey` is no longer overlaid here. The Store's initial state
   * (via `createInitialState`) seeds `sortKey: 'best-selling'`, and MMM has
   * no UI to mutate it, so `storeState.sortKey` is always the single source of
   * truth that `evaluateHead` / `evaluateTail` consult to call
   * `SortIndex.getSortedIds(state.sortKey)`. Removing the redundant overlay
   * means MMM has zero local sort decision; sort routes entirely through the
   * shared comparator chain (sort-shared → SortIndex → orchestrator).
   *
   * Called from the orchestrator adapter in the constructor so every
   * dispatcher-driven head/tail evaluate sees the latest DOM filter intent
   * without requiring MMM to persist `activeFilters` into the reducer via
   * toggle-semantics commands (which don't match MMM's UI shape).
   */
  _composeOrchestratorState(storeState) {
    const vehicle = this.vehicleApi?.activeVehicleDisplayData || null;
    const vehicleId = vehicle?.id || null;
    const compatOnly = !!this.compatibilityToggle?.checked && !!vehicleId;
    const selectedTypeValue = this.getSelectedTypeValue();
    const authorityState = this.getAuthorityState({
      vehicleId,
      compatOnly,
      selectedTypeValue
    });
    return {
      ...storeState,
      activeFilters: authorityState.activeFilters
    };
  }

  async buildRenderCompatibilityContext(pageProductIds = [], vehicle = null, compatOnly = false) {
    if (!vehicle?.id) {
      return null;
    }

    if (compatOnly) {
      return new Map(pageProductIds.map((productId) => [productId, true]));
    }

    return this.getPageCompatibilityMap(pageProductIds, vehicle.id);
  }

  getPageHref(page) {
    const url = new URL(window.location.href);

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    return `${url.pathname}${url.search}${url.hash}`;
  }

  syncCurrentPage(totalItemCount, resetPage = false) {
    // Phase 1 — page is owned by the reducer. Compute the next page here and
    // dispatch through the shared bus so `gridGeneration` and `currentPage`
    // stay on the same atomic commit path as every other page change.
    let nextPage;
    if (resetPage) {
      nextPage = 1;
    } else {
      const totalPages = Math.max(1, Math.ceil((totalItemCount || 0) / ITEMS_PER_PAGE));
      const current = this.store.getState().currentPage || 1;
      nextPage = Math.min(Math.max(current, 1), totalPages);
    }
    if (nextPage !== this.store.getState().currentPage) {
      bus.dispatch(pageChangeRequestCommand(nextPage));
    }
  }

  updateCurrentPageUrl() {
    if (typeof window.history?.replaceState !== 'function') {
      return;
    }

    window.history.replaceState({}, '', this.getPageHref(this.store.getState().currentPage));
  }

  async renderResultsPage() {
    if (!this.productsContainer) {
      return;
    }

    // Phase 1 — read the visible slice and current page from the shared store.
    const state = this.store.getState();
    const currentPage = Number.isFinite(state.currentPage) ? state.currentPage : 1;
    const visibleProductIds = Array.isArray(state.visibleProductIds)
      ? state.visibleProductIds
      : [];
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const pageProductIds = visibleProductIds.slice(startIndex, endIndex);

    // Phase 3 — signature gate short-circuit. If the current page slice
    // matches the last committed paint we emit `render-skipped` and return
    // BEFORE paying the cost of compat resolution + fragment rebuild. This
    // is the MMM side of the collection page's "tail commits with unchanged
    // first-page IDs are no-ops" invariant.
    const signature = this.signatureGate.computeSignature(pageProductIds, currentPage);
    if (!this.signatureGate.shouldRender(signature)) {
      this.signatureGate.emitSkipped('signature-unchanged', {
        signature,
        page: currentPage,
        count: pageProductIds.length
      });
      return;
    }

    const renderToken = ++this.pageRenderToken;
    const { vehicle, compatOnly } = this.currentRenderContext;
    const compatibilityByProduct = await this.buildRenderCompatibilityContext(pageProductIds, vehicle, compatOnly);

    if (renderToken !== this.pageRenderToken) {
      return;
    }

    const fragment = document.createDocumentFragment();

    pageProductIds.forEach((productId) => {
      const product = this.catalogProducts.get(productId);
      if (!product) {
        return;
      }

      const element = createProductElement(product);
      if (!element) {
        return;
      }

      const compatibilityText = element.querySelector('[data-attachment-product-compatibility]');
      this.renderProductCompatibility(
        { compatibilityText },
        vehicle?.id ? compatibilityByProduct?.get(productId) === true : null,
        vehicle
      );

      fragment.appendChild(element);
    });

    this.productsContainer.replaceChildren(fragment);
    this.productsContainer.hidden = false;
    this.productsContainer.removeAttribute('aria-busy');
    this.updateCurrentPageUrl();

    // Phase 3 — commit the signature AFTER the DOM mutation lands so a
    // subsequent identical slice/page dispatch short-circuits at
    // `shouldRender`. Emit `render-complete` so probes can attribute the
    // paint to MMM (same event name collection page uses).
    this.signatureGate.commit(signature);
    this.signatureGate.emitComplete();

    debug.info('Rendered selective result page', {
      currentPage,
      renderedCount: pageProductIds.length,
      totalVisible: visibleProductIds.length
    });
  }

  renderPagination(totalItemCount) {
    if (!this.pagination) {
      return;
    }

    const totalPages = Math.ceil(totalItemCount / ITEMS_PER_PAGE);
    if (totalPages <= 1) {
      this.pagination.innerHTML = '';
      this.pagination.setAttribute('hidden', '');
      return;
    }

    this.pagination.innerHTML = this.generatePaginationHtml(totalPages, this.store.getState().currentPage);
    this.pagination.removeAttribute('hidden');
  }

  generatePaginationHtml(totalPages, currentPage) {
    const items = [];
    const { start, end } = this.calculatePaginationRange(totalPages, currentPage);

    if (currentPage > 1) {
      items.push(this.renderPaginationLink(currentPage - 1, 'Previous', 'attachment-wizard-pagination-prev'));
    }

    if (start > 1) {
      items.push(this.renderPaginationLink(1, '1'));
      if (start > 2) {
        items.push('<li><span class="pagination__item">...</span></li>');
      }
    }

    for (let page = start; page <= end; page += 1) {
      if (page === currentPage) {
        items.push(`
          <li>
            <span class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ${page}">
              ${page}
            </span>
          </li>
        `);
      } else {
        items.push(this.renderPaginationLink(page, String(page)));
      }
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        items.push('<li><span class="pagination__item">...</span></li>');
      }
      items.push(this.renderPaginationLink(totalPages, String(totalPages)));
    }

    if (currentPage < totalPages) {
      items.push(this.renderPaginationLink(currentPage + 1, 'Next', 'attachment-wizard-pagination-next'));
    }

    return `
      <nav class="pagination" role="navigation" aria-label="Attachment results pages">
        <ul class="pagination__list list-unstyled" role="list">
          ${items.join('')}
        </ul>
      </nav>
    `;
  }

  renderPaginationLink(page, label, extraClass = '') {
    const className = ['pagination__item', 'link', extraClass].filter(Boolean).join(' ');
    const ariaLabel = label === 'Previous'
      ? 'Previous page'
      : label === 'Next'
        ? 'Next page'
        : `Page ${label}`;

    return `
      <li>
        <a
          href="${escapeHtml(this.getPageHref(page))}"
          class="${className}"
          aria-label="${ariaLabel}"
          data-attachment-wizard-page="${page}"
        >
          ${label}
        </a>
      </li>
    `;
  }

  calculatePaginationRange(totalPages, currentPage) {
    const spread = Math.max(1, MAX_PAGINATION_LINKS - 1);
    let start = Math.max(1, currentPage - Math.floor(spread / 2));
    let end = Math.min(totalPages, start + spread);

    if ((end - start) < spread) {
      start = Math.max(1, end - spread);
    }

    return { start, end };
  }

  async applyAuthorityResult({
    result,
    vehicle,
    compatOnly,
    selectedType,
    resetPage = false,
    phase = 'full',
    reason = 'view-update'
  } = {}) {
    const effectiveSelectedType = phase === 'full'
      ? this.syncProductTypeOptionsFromFacetState(result?.facetState, selectedType, {
        compatOnly,
        reason
      })
      : selectedType;
    const visibleProductIds = Array.isArray(result?.visibleProductIds)
      ? result.visibleProductIds.filter((productId) => this.catalogProducts.has(productId))
      : [];
    const productTypeCount = Array.isArray(result?.facetState?.productTypes)
      ? result.facetState.productTypes.length
      : null;
    const typeOptions = Array.from(this.productTypeFilter?.options || []).map((option) => ({
      value: option.value || '',
      label: (option.dataset?.typeLabel || option.textContent || '').trim()
    }));
    const authorityPayload = {
      phase,
      reason,
      source: result?.source || null,
      generation: result?.generation || null,
      durationMs: result?.durationMs ?? null,
      compatOnly,
      selectedType: effectiveSelectedType,
      visibleCount: visibleProductIds.length,
      activeBaseCount: Array.isArray(result?.activeBaseProductIds)
        ? result.activeBaseProductIds.length
        : Number(result?.activeBaseCount || 0),
      productTypeCount,
      typeOptions,
      quickPending: phase === 'quick'
    };

    if (phase === 'full' && selectedType && effectiveSelectedType !== selectedType) {
      recordAuthorityProbe({
        ...authorityPayload,
        previousSelectedType: selectedType,
        selectionResetPending: true
      });
      recordAuthorityProbe({
        phase: 'type-reset',
        reason,
        source: result?.source || null,
        previousSelectedType: selectedType,
        selectedType: effectiveSelectedType,
        compatOnly,
        visibleCount: result?.visibleProductIds?.length || 0
      });
      await this.applyFilters({
        resetPage: true,
        reason: 'type-reset-after-compatibility',
        allowQuick: true
      });
      return;
    }

    if (result?.source === 'full-hydration') {
      this.scheduleCompactIndexBackfill({
        reason: `authority-${reason || 'view-update'}`,
        compactIndexFailure: result?.fallback?.message || result?.fallback?.reason || null
      });
    }

    this.currentRenderContext = {
      compatibilityByProduct: null,
      vehicle,
      compatOnly,
      selectedType: effectiveSelectedType,
      resultSource: result?.source || null,
      resultMode: phase,
      quickPending: phase === 'quick'
    };

    // Phase 1 — authority result writes back through the shared reducer.
    // Full-phase results use CMD_EXACT_AUTHORITY_READY (atomic commit of
    // visibleProductIds, allProductIds, activeBaseProductIds, viewStats,
    // facetState, paginationItemCount, totalProductCount). Quick-phase uses
    // the lighter CMD_EXACT_AUTHORITY_QUICK_READY which only updates the
    // visible slice without touching facet/stats.
    const totalProductCount = this.catalogProducts.size;
    const allProductIds = Array.isArray(this.catalogIdOrder) && this.catalogIdOrder.length
      ? this.catalogIdOrder
      : visibleProductIds;
    const activeBaseProductIds = Array.isArray(result?.activeBaseProductIds)
      ? result.activeBaseProductIds
      : (compatOnly ? visibleProductIds : allProductIds);
    const generation = Number.isFinite(result?.generation)
      ? result.generation
      : (this.store.getState().gridGeneration || 0);
    const source = result?.source || 'mmm-exact-authority';
    const resolvedAt = Date.now();

    try {
      if (phase === 'full') {
        const defaultFacetState = {
          productTypes: [],
          priceBounds: { minCents: 0, maxCents: 100000 },
          deliveryBounds: { maxDays: 30 },
          listFilters: new Map(),
          customFilters: new Map()
        };
        const defaultViewStats = {
          minCents: 0,
          maxCents: 100000,
          maxDays: 30
        };
        bus.dispatch(exactAuthorityReadyCommand({
          visibleProductIds,
          allProductIds,
          activeBaseProductIds,
          viewStats: (result?.viewStats && typeof result.viewStats === 'object')
            ? result.viewStats
            : defaultViewStats,
          facetState: (result?.facetState && typeof result.facetState === 'object')
            ? result.facetState
            : defaultFacetState,
          paginationItemCount: visibleProductIds.length,
          totalProductCount,
          generation,
          source,
          fallback: result?.fallback || null,
          resolvedAt
        }));
      } else {
        bus.dispatch(exactAuthorityQuickReadyCommand({
          visibleProductIds,
          totalProductCount,
          paginationItemCount: visibleProductIds.length,
          activeBaseCount: Array.isArray(activeBaseProductIds)
            ? activeBaseProductIds.length
            : Number(result?.activeBaseCount || visibleProductIds.length),
          generation,
          source,
          resolvedAt
        }));
      }
    } catch (error) {
      debug.warn('MMM authority-ready dispatch failed; store will drift until next commit.', error);
    }

    // Clamp current page against the new visible total. `syncCurrentPage`
    // dispatches CMD_PAGE_CHANGE_REQUEST only when the page actually moves;
    // otherwise this is a no-op.
    this.syncCurrentPage(visibleProductIds.length, resetPage);
    // Cold-boot gate: clear as soon as a real authority commit lands — for
    // the quick phase only once the first non-empty slice (the first 48
    // compatible products produced by HeadTailDispatcher/evaluateHead) is
    // ready, and always on the full phase since that is the authoritative
    // final count. This lets the summary text and the grid reveal together
    // on the first meaningful paint instead of waiting for init() to
    // settle.
    if (phase === 'full' || visibleProductIds.length > 0) {
      document.documentElement.classList.remove('mmm-cold-boot-pending');
    }
    // Summary is not yet subscription-driven in Phase 1 — fire imperatively.
    this.renderSummary(visibleProductIds.length, vehicle, compatOnly, effectiveSelectedType);

    recordAuthorityProbe(authorityPayload);
  }

  requestFilterApply(options = {}) {
    const pending = this.pendingFilterRequest || {};
    this.pendingFilterRequest = {
      resetPage: pending.resetPage === true || options.resetPage === true,
      reason: options.reason || pending.reason || 'view-update',
      allowQuick: pending.allowQuick === true || options.allowQuick === true
    };

    if (this.filterFlushTimer || this.filterFlushInFlight) {
      return;
    }

    this.filterFlushTimer = window.setTimeout(() => {
      this.filterFlushTimer = null;
      void this.flushFilterRequests();
    }, 0);
  }

  async flushFilterRequests() {
    if (this.filterFlushInFlight) {
      return;
    }

    this.filterFlushInFlight = true;

    try {
      while (this.pendingFilterRequest) {
        const request = this.pendingFilterRequest;
        this.pendingFilterRequest = null;
        await this.applyFilters(request);
      }
    } catch (error) {
      debug.warn('Failed to flush MMM filter request queue.', error);
    } finally {
      this.filterFlushInFlight = false;
      if (this.pendingFilterRequest && !this.filterFlushTimer) {
        this.filterFlushTimer = window.setTimeout(() => {
          this.filterFlushTimer = null;
          void this.flushFilterRequests();
        }, 0);
      }
    }
  }

  async applyFilters({
    resetPage = false,
    reason = 'view-update',
    allowQuick = false
  } = {}) {
    const currentRunToken = ++this.filterRunToken;

    // Phase 2 — schedule an optimistic head/tail pair before awaiting the
    // catalog/authority pipeline. The HeadTailDispatcher rAF-coalesces
    // bursts of triggers into a single head + tail dispatch, so rapid
    // compat-toggle / type-select interactions emit one pair of
    // OptimisticHeadReady + OptimisticTailReady commands per frame. The
    // reducer's gridGeneration guard and matcherReady backstop reject any
    // stale commits that land after the authoritative ExactAuthority
    // corrections. Scheduling here (rather than waiting for
    // `catalogHydrationPromise`) is the whole point: the head/tail walk
    // runs against the live `compactIndexStore` and gives the grid a
    // first paint on the same tick as the user click, collapsing the
    // pre-Phase-2 "click → await hydration → await worker → paint" gap.
    if (this._headTailDispatcher && compactIndexStore.size() > 0) {
      this._headTailDispatcher.scheduleFromTrigger(reason || 'view-update');
    }

    if (this.catalogHydrationPromise && !this.catalogReady) {
      this.setStatus(STATUS_TEXT.LOADING);
      await Promise.allSettled([this.catalogHydrationPromise]);
      if (currentRunToken !== this.filterRunToken) return;
    }

    const vehicle = this.vehicleApi?.activeVehicleDisplayData || null;
    const vehicleId = vehicle?.id || null;
    const compatOnly = !!this.compatibilityToggle?.checked && !!vehicleId;
    const selectedType = this.getSelectedTypeValue();

    if (!this.catalogIdOrder.length) {
      const effectiveSelectedType = this.syncProductTypeOptionsFromFacetState({}, selectedType);
      this.currentRenderContext = {
        compatibilityByProduct: null,
        vehicle,
        compatOnly,
        selectedType: effectiveSelectedType,
        resultSource: null,
        resultMode: 'empty',
        quickPending: false
      };
      // Phase 1 — publish empty result through the shared reducer. The
      // subscriber will re-paint grid + pagination; summary remains
      // imperative for now.
      bus.dispatch(exactAuthorityQuickReadyCommand({
        visibleProductIds: [],
        totalProductCount: 0,
        paginationItemCount: 0,
        activeBaseCount: 0,
        generation: this.store.getState().gridGeneration || 0,
        source: 'mmm-empty-catalog',
        resolvedAt: Date.now()
      }));
      this.syncCurrentPage(0, resetPage);
      this.renderSummary(0, vehicle, compatOnly, effectiveSelectedType);
      return;
    }

    if (!this.exactAuthority) {
      debug.warn('Exact authority unavailable for MMM; rendering existing catalog without shared authority refinement.');
      const fallbackIds = [...this.catalogIdOrder];
      this.currentRenderContext = {
        compatibilityByProduct: null,
        vehicle,
        compatOnly,
        selectedType,
        resultSource: 'local-fallback',
        resultMode: 'fallback',
        quickPending: false
      };
      bus.dispatch(exactAuthorityQuickReadyCommand({
        visibleProductIds: fallbackIds,
        totalProductCount: fallbackIds.length,
        paginationItemCount: fallbackIds.length,
        activeBaseCount: fallbackIds.length,
        generation: this.store.getState().gridGeneration || 0,
        source: 'mmm-local-fallback',
        resolvedAt: Date.now()
      }));
      this.syncCurrentPage(fallbackIds.length, resetPage);
      this.renderSummary(fallbackIds.length, vehicle, compatOnly, selectedType);
      return;
    }

    this.exactAuthority.abortEvaluation(currentRunToken - 1);
    this.setStatus(vehicleId ? STATUS_TEXT.MATCHING : STATUS_TEXT.FILTERING);

    const authorityState = this.getAuthorityState({
      vehicleId,
      compatOnly,
      selectedTypeValue: selectedType
    });
    const evaluationRequest = {
      state: authorityState,
      generation: currentRunToken,
      reason,
      isAborted: () => currentRunToken !== this.filterRunToken
    };

    let result;
    try {
      result = allowQuick
        ? await this.exactAuthority.evaluateUserRefine(evaluationRequest, {
          onQuickResult: (quickResult) => {
            if (currentRunToken !== this.filterRunToken) {
              return;
            }
            void this.applyAuthorityResult({
              result: quickResult,
              vehicle,
              compatOnly,
              selectedType,
              resetPage,
              phase: 'quick',
              reason
            }).catch((error) => {
              debug.warn('Failed to apply MMM quick authority result.', error);
            });
          }
        })
        : await this.exactAuthority.evaluate(evaluationRequest);
    } catch (error) {
      if (error instanceof EvaluationAbortedError || error?.name === 'EvaluationAbortedError') {
        debug.debug('Ignoring superseded MMM exact-authority generation.', {
          generation: currentRunToken,
          reason,
          phase: error?.phase || null
        });
        return;
      }
      throw error;
    }

    if (currentRunToken !== this.filterRunToken) {
      return;
    }

    await this.applyAuthorityResult({
      result,
      vehicle,
      compatOnly,
      selectedType,
      resetPage,
      phase: 'full',
      reason
    });
  }

  renderProductCompatibility(product, compatible, vehicle) {
    if (!product.compatibilityText) return;

    product.compatibilityText.classList.remove('is-compatible', 'is-incompatible', 'is-pending');

    if (!vehicle?.id) {
      product.compatibilityText.textContent = 'Select a machine to confirm compatibility.';
      product.compatibilityText.classList.add('is-pending');
      return;
    }

    if (compatible) {
      product.compatibilityText.textContent = `Compatible with ${vehicle.displayName}`;
      product.compatibilityText.classList.add('is-compatible');
      return;
    }

    product.compatibilityText.textContent = `No confirmed fit for ${vehicle.displayName}`;
    product.compatibilityText.classList.add('is-incompatible');
  }

  renderSummary(visibleCount, vehicle, compatOnly, selectedType) {
    if (!this.summary) return;

    // Cold-boot gate: suppress count-based summaries while the cold-boot
    // class is still on <html>. Prevents the "Showing 0 attachments" flash
    // that happens when an early quick-path or empty-catalog branch fires
    // before the vehicle-filtered authority commit lands. The gate is
    // cleared on the first real authority commit (applyAuthorityResult) or
    // in the init() finally, whichever comes first, so the next call will
    // paint the true count.
    if (document.documentElement.classList.contains('mmm-cold-boot-pending')) {
      return;
    }

    const typeMessage = selectedType
      ? ` in ${this.productTypeFilter?.selectedOptions?.[0]?.textContent || 'this type'}`
      : '';

    if (!vehicle?.id) {
      this.summary.textContent = `Showing ${visibleCount} attachments${typeMessage}. Select a machine to turn on compatibility filtering.`;
      return;
    }

    if (compatOnly) {
      this.summary.textContent = `Showing ${visibleCount} compatible attachments${typeMessage} for ${vehicle.displayName}.`;
      return;
    }

    this.summary.textContent = `Showing ${visibleCount} attachments${typeMessage} for ${vehicle.displayName}. Compatibility is displayed on each result.`;
  }
}

function initializeAttachmentWizard() {
  document.querySelectorAll('[data-attachment-wizard]').forEach((root) => {
    const wizard = new AttachmentWizard(root);
    wizard.init().catch((error) => {
      console.error('[Machine Match Maker] Initialization failed.', error);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAttachmentWizard);
} else {
  initializeAttachmentWizard();
}
